import { useMemo } from 'react';

import { TaxonomyType } from '@revelio/data-access';

import { SKILL_REFERENCE_TAXONOMY_TREE_LEVELS } from '../../../next/CustomDash/filters/utils/skillReferenceTaxonomyTree';
import { useSkillTaxonomy } from '../../user-preferences/hooks/useSkillTaxonomySelection';
import { useTaxonomyTree } from './useTaxonomyTree';

export type DepthToLabelMap = Record<number, string>;

/**
 * Derives a depth -> label mapping for custom skill taxonomy levels from the
 * taxonomy tree API response. This avoids storing duplicated metadata on each
 * tree node while still enabling dynamic branch labels and chip titles.
 */
export const useCustomSkillLevelLabels = (
  taxonomyId?: number,
  taxonomyVersionOverride?: string
) => {
  const prefTaxonomyId = useSkillTaxonomy();
  const resolvedTaxonomyId = taxonomyId ?? prefTaxonomyId ?? 0;

  const { levels: skillTaxonomyReferenceLevels } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Skill,
    taxonomyId: resolvedTaxonomyId,
    taxonomyVersion: taxonomyVersionOverride,
    levels: [...SKILL_REFERENCE_TAXONOMY_TREE_LEVELS],
  });

  const labelsByDepth: DepthToLabelMap | null = useMemo(() => {
    if (skillTaxonomyReferenceLevels.length === 0) return null;
    const sorted = [...skillTaxonomyReferenceLevels].sort(
      (a, b) => a.id - b.id
    );
    const map: DepthToLabelMap = {};
    sorted.forEach((level, index) => {
      const label = level.label ?? level.tag ?? `Level ${level.id}`;
      if (label !== '') map[index] = label;
    });
    return Object.keys(map).length > 0 ? map : null;
  }, [skillTaxonomyReferenceLevels]);

  return { labelsByDepth };
};
